"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, ScreenShare } from "lucide-react";
import { useCallStore } from "../model/call-store";
import { callEngine } from "../model/call-engine";
import { useCall } from "../model/use-call";
import { useConversations } from "../model/use-conversations";
import { useTeammates } from "../model/use-teammates";
import type { RosterFace } from "../model/meeting-roster";
import { LiveDot } from "./live-dot";
import { CallControls } from "./call-controls";
import { CallInvitePicker } from "./call-invite-picker";
import { StageShell } from "./stage-shell";
import { StageTile } from "./stage-tile";
import { PanelFace } from "./panel-face";
const two = (n: number): string => n.toString().padStart(2, "0");
const formatDuration = (secs: number): string => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${two(m)}:${two(s)}`;
};

/**
 * CallStage — the in-call UI for direct calls, replacing the old minimal CallBar
 * (call-parity spec 2026-07-24-001 §11.3). Same StageShell frame as space
 * meetings: floating panel by default, full-screen stage on expand, with screen
 * share available to both parties. Rendered by CallBridge (mounted app-wide) so
 * the call survives navigation.
 *
 * Face names: the 1:1 counterparty label comes from the cached conversations
 * list; unknown attendees (Phase 2 invitees before their name resolves) fall
 * back to "Guest", mirroring resolveRoster.
 */
export function CallStage() {
  const $ = _c(116);
  const phase = useCallStore(_temp);
  const expanded = useCallStore(_temp2);
  const conversationId = useCallStore(_temp3);
  const peers = useCallStore(_temp4);
  const isMuted = useCallStore(_temp5);
  const connectedAtMs = useCallStore(_temp6);
  const isSharing = useCallStore(_temp7);
  const shareTileId = useCallStore(_temp8);
  const sharePresenterId = useCallStore(_temp9);
  const shareLabel = useCallStore(_temp10);
  const shareSurface = useCallStore(_temp11);
  const errorMessage = useCallStore(_temp12);
  const patch = useCallStore(_temp13);
  const {
    hangup,
    toggleMute,
    invite
  } = useCall();
  const [inviteOpen, setInviteOpen] = useState(false);
  const active = phase === "outgoing" || phase === "connecting" || phase === "connected" || phase === "ended";
  const {
    conversations
  } = useConversations();
  let t0;
  if ($[0] !== conversationId || $[1] !== conversations) {
    let t1;
    if ($[3] !== conversationId) {
      t1 = c => c.conversationId === conversationId;
      $[3] = conversationId;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = conversations.find(t1);
    $[0] = conversationId;
    $[1] = conversations;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const row = t0;
  const counterpartyName = row?.counterpartyLabel ?? row?.title ?? null;
  const {
    data: teammates
  } = useTeammates();
  let t1;
  if ($[5] !== teammates) {
    t1 = new Map((teammates ?? []).map(_temp14));
    $[5] = teammates;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const byId = t1;
  let t2;
  if ($[7] !== byId || $[8] !== peers) {
    let t3;
    if ($[10] !== byId) {
      t3 = p => p.userId === null || !byId.has(p.userId);
      $[10] = byId;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t2 = peers.filter(t3);
    $[7] = byId;
    $[8] = peers;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const unknowns = t2;
  let t3;
  if ($[12] !== byId || $[13] !== counterpartyName || $[14] !== unknowns) {
    t3 = userId => {
      if (userId !== null && byId.has(userId)) {
        return byId.get(userId) as string;
      }
      if (unknowns.length === 1 && counterpartyName) {
        return counterpartyName;
      }
      return "Guest";
    };
    $[12] = byId;
    $[13] = counterpartyName;
    $[14] = unknowns;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const resolvePeerName = t3;
  let t4;
  if ($[16] !== isMuted) {
    t4 = {
      key: "self",
      name: "You",
      monogram: "Y",
      isMe: true,
      muted: isMuted,
      speaking: false
    };
    $[16] = isMuted;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const self = t4;
  let t5;
  if ($[18] !== peers || $[19] !== resolvePeerName || $[20] !== self) {
    let t6;
    if ($[22] !== resolvePeerName) {
      t6 = p_0 => {
        const name = resolvePeerName(p_0.userId);
        return {
          key: p_0.attendeeId,
          name,
          monogram: (name.charAt(0) || "?").toUpperCase(),
          isMe: false,
          muted: p_0.muted,
          speaking: p_0.speaking
        };
      };
      $[22] = resolvePeerName;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    const remote = peers.map(t6);
    t5 = [self, ...remote];
    $[18] = peers;
    $[19] = resolvePeerName;
    $[20] = self;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const faces = t5;
  const shareActive = shareTileId != null;
  const iAmPresenter = isSharing;
  let t6;
  if ($[24] !== iAmPresenter || $[25] !== resolvePeerName || $[26] !== sharePresenterId) {
    t6 = iAmPresenter ? "You" : resolvePeerName(sharePresenterId);
    $[24] = iAmPresenter;
    $[25] = resolvePeerName;
    $[26] = sharePresenterId;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const presenterName = t6;
  const canSeeShareVideo = iAmPresenter ? isSharing : shareActive;
  const previewWillMirror = iAmPresenter && shareSurface === "monitor";
  const [previewOverride, setPreviewOverride] = useState(null);
  const showPresenterPreview = previewOverride ?? true;
  const [shareWasOn, setShareWasOn] = useState(canSeeShareVideo);
  if (shareWasOn !== canSeeShareVideo) {
    setShareWasOn(canSeeShareVideo);
    if (!canSeeShareVideo) {
      setPreviewOverride(null);
    }
  }
  const shareVideoRef = _temp15;
  const [nowMs, setNowMs] = useState(0);
  let t7;
  let t8;
  if ($[28] !== phase) {
    t7 = () => {
      if (phase !== "connected") {
        return;
      }
      const id = setInterval(() => setNowMs(Date.now()), 500);
      return () => clearInterval(id);
    };
    t8 = [phase];
    $[28] = phase;
    $[29] = t7;
    $[30] = t8;
  } else {
    t7 = $[29];
    t8 = $[30];
  }
  useEffect(t7, t8);
  const elapsed = phase === "connected" && connectedAtMs ? Math.max(0, Math.floor((nowMs - connectedAtMs) / 1000)) : 0;
  if (!active) {
    return null;
  }
  const title = counterpartyName ?? "Call";
  let t9;
  if ($[31] !== elapsed || $[32] !== errorMessage || $[33] !== phase) {
    t9 = phase === "connected" ? formatDuration(elapsed) : phase === "ended" ? errorMessage ?? "Call ended" : phase === "connecting" ? "Connecting\u2026" : "Calling\u2026";
    $[31] = elapsed;
    $[32] = errorMessage;
    $[33] = phase;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const meta = t9;
  let t10;
  if ($[35] !== peers) {
    t10 = new Set(peers.map(_temp16).filter(_temp17));
    $[35] = peers;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const onCallUserIds = t10;
  let t11;
  if ($[37] !== invite || $[38] !== inviteOpen || $[39] !== onCallUserIds) {
    t11 = inviteOpen ? <CallInvitePicker excludeUserIds={onCallUserIds} onPick={userId_0 => {
      setInviteOpen(false);
      invite(userId_0);
    }} onClose={() => setInviteOpen(false)} /> : null;
    $[37] = invite;
    $[38] = inviteOpen;
    $[39] = onCallUserIds;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  const t12 = shareActive && !iAmPresenter;
  const t13 = phase === "connected";
  let t14;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setInviteOpen(_temp18);
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== hangup) {
    t15 = () => void hangup();
    $[42] = hangup;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== inviteOpen || $[45] !== isMuted || $[46] !== isSharing || $[47] !== t12 || $[48] !== t13 || $[49] !== t15 || $[50] !== toggleMute) {
    t16 = <CallControls isMuted={isMuted} isSharing={isSharing} shareBlocked={t12} canInvite={t13} inviteOpen={inviteOpen} onToggleInvite={t14} onToggleMute={toggleMute} onHangup={t15} />;
    $[44] = inviteOpen;
    $[45] = isMuted;
    $[46] = isSharing;
    $[47] = t12;
    $[48] = t13;
    $[49] = t15;
    $[50] = toggleMute;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== t11 || $[53] !== t16) {
    t17 = <div className="flex flex-col items-center gap-2">{t11}{t16}</div>;
    $[52] = t11;
    $[53] = t16;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  const controls = t17;
  let t18;
  if ($[55] !== patch) {
    t18 = next => patch({
      expanded: next
    });
    $[55] = patch;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  const t19 = phase === "connected" ? "live" : "joining";
  let t20;
  if ($[57] !== t19) {
    t20 = <LiveDot phase={t19} />;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== title) {
    t21 = <h2 className="truncate text-sm font-semibold">{title}</h2>;
    $[59] = title;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== meta) {
    t22 = <p className="text-xs text-white/50">{meta}</p>;
    $[61] = meta;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t21 || $[64] !== t22) {
    t23 = <div className="min-w-0 flex-1">{t21}{t22}</div>;
    $[63] = t21;
    $[64] = t22;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== t20 || $[67] !== t23) {
    t24 = <>{t20}{t23}</>;
    $[66] = t20;
    $[67] = t23;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  const t25 = phase === "connected" ? "live" : "joining";
  let t26;
  if ($[69] !== t25) {
    t26 = <LiveDot phase={t25} />;
    $[69] = t25;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== title) {
    t27 = <p className="truncate text-sm font-semibold">{title}</p>;
    $[71] = title;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== meta) {
    t28 = <p className="text-[11px] tabular-nums text-white/50">{meta}</p>;
    $[73] = meta;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== t27 || $[76] !== t28) {
    t29 = <div className="min-w-0 flex-1">{t27}{t28}</div>;
    $[75] = t27;
    $[76] = t28;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== t26 || $[79] !== t29) {
    t30 = <>{t26}{t29}</>;
    $[78] = t26;
    $[79] = t29;
    $[80] = t30;
  } else {
    t30 = $[80];
  }
  let t31;
  if ($[81] !== canSeeShareVideo || $[82] !== faces || $[83] !== iAmPresenter || $[84] !== meta || $[85] !== phase || $[86] !== previewWillMirror || $[87] !== shareActive || $[88] !== shareLabel || $[89] !== showPresenterPreview) {
    t31 = iAmPresenter ? <div className="flex flex-col items-center gap-5"><p className="flex items-center gap-2 text-sm font-medium text-[#c8c5ff]"><ScreenShare className="h-4 w-4" aria-hidden={true} />{shareActive ? `You’re sharing ${shareLabel ?? "your screen"}` : "Starting your screen share\u2026"}</p><div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">{faces.map(_temp19)}</div>{canSeeShareVideo && showPresenterPreview ? <figure className="flex flex-col items-center gap-1.5"><video ref={shareVideoRef} autoPlay={true} playsInline={true} muted={true} data-testid="call-share-video" className="w-64 max-w-full rounded-lg bg-black object-contain ring-1 ring-white/15 sm:w-80" /><figcaption className="text-[11px] text-white/40">{previewWillMirror ? "Repeats itself \u2014 a page can\u2019t hide from a full-screen capture" : "Preview of what they see"}</figcaption></figure> : null}{canSeeShareVideo ? <button type="button" onClick={() => setPreviewOverride(!showPresenterPreview)} aria-pressed={showPresenterPreview} data-testid="call-share-preview-toggle" className="flex items-center gap-1.5 rounded-full bg-white/6 px-3 py-1.5 text-[11px] font-medium text-white/70 transition-all active:scale-[0.97] hover:bg-white/10 hover:text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80">{showPresenterPreview ? <EyeOff className="h-3.5 w-3.5" aria-hidden={true} /> : <Eye className="h-3.5 w-3.5" aria-hidden={true} />}{showPresenterPreview ? "Hide preview" : "Show preview"}{previewWillMirror && !showPresenterPreview ? <span className="text-white/40">· share a window for a clean preview</span> : null}</button> : null}</div> : canSeeShareVideo ? <div className="flex h-full w-full flex-col gap-3"><video ref={shareVideoRef} autoPlay={true} playsInline={true} muted={true} data-testid="call-share-video" className="min-h-0 w-full flex-1 rounded-xl bg-black object-contain" /><div className="flex shrink-0 flex-wrap items-center justify-center gap-3">{faces.map(_temp20)}</div></div> : <div className="flex flex-col items-center gap-4">{iAmPresenter ? <div className="flex flex-col items-center gap-1" data-testid="call-share-self-status"><p className="flex items-center gap-2 text-sm font-medium text-[#c8c5ff]"><ScreenShare className="h-4 w-4" aria-hidden={true} />{shareActive ? "You\u2019re sharing your screen" : "Starting your screen share\u2026"}</p>{shareLabel ? <p className="max-w-xs truncate text-xs text-white/50" title={shareLabel}>Sharing: {shareLabel}</p> : null}</div> : null}<div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">{faces.map(_temp21)}</div>{phase !== "connected" ? <p className="text-sm text-white/40">{meta}</p> : null}</div>;
    $[81] = canSeeShareVideo;
    $[82] = faces;
    $[83] = iAmPresenter;
    $[84] = meta;
    $[85] = phase;
    $[86] = previewWillMirror;
    $[87] = shareActive;
    $[88] = shareLabel;
    $[89] = showPresenterPreview;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  let t32;
  if ($[91] !== canSeeShareVideo || $[92] !== iAmPresenter || $[93] !== shareLabel) {
    t32 = iAmPresenter && !canSeeShareVideo ? <p className="flex items-center gap-1.5 rounded-lg bg-[#5651ff]/20 px-2.5 py-1.5 text-[11px] font-medium text-[#c8c5ff]"><ScreenShare className="h-3.5 w-3.5 shrink-0" aria-hidden={true} /><span className="truncate" title={shareLabel ?? undefined}>{shareLabel ? `Sharing ${shareLabel}` : "You\u2019re sharing your screen"}</span></p> : null;
    $[91] = canSeeShareVideo;
    $[92] = iAmPresenter;
    $[93] = shareLabel;
    $[94] = t32;
  } else {
    t32 = $[94];
  }
  let t33;
  if ($[95] !== canSeeShareVideo || $[96] !== iAmPresenter || $[97] !== patch || $[98] !== presenterName || $[99] !== shareActive) {
    t33 = canSeeShareVideo ? <button type="button" onClick={() => patch({
      expanded: true
    })} title="Expand to full screen" className="group relative overflow-hidden rounded-lg ring-1 ring-white/10 transition-all active:scale-[0.99] hover:ring-white/30 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80"><video ref={shareVideoRef} autoPlay={true} playsInline={true} muted={true} data-testid="call-share-video" className="aspect-video w-full bg-black object-contain" /><span className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-linear-to-t from-black/80 to-transparent px-2 pb-1.5 pt-4 text-[11px] font-medium text-white"><ScreenShare className="h-3 w-3 shrink-0" aria-hidden={true} /><span className="truncate">{iAmPresenter ? "You\u2019re sharing" : `${presenterName} sharing`}</span></span></button> : shareActive && !iAmPresenter ? <button type="button" onClick={() => patch({
      expanded: true
    })} className="flex items-center gap-1.5 rounded-lg bg-[#5651ff]/20 px-2.5 py-1.5 text-left text-[11px] font-medium text-[#c8c5ff] transition-colors hover:bg-[#5651ff]/30 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80"><ScreenShare className="h-3.5 w-3.5 shrink-0" aria-hidden={true} /><span className="truncate">{presenterName} sharing screen — expand to view</span></button> : null;
    $[95] = canSeeShareVideo;
    $[96] = iAmPresenter;
    $[97] = patch;
    $[98] = presenterName;
    $[99] = shareActive;
    $[100] = t33;
  } else {
    t33 = $[100];
  }
  let t34;
  if ($[101] !== faces) {
    t34 = <div className="flex flex-wrap gap-x-3 gap-y-2">{faces.map(_temp22)}</div>;
    $[101] = faces;
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  let t35;
  if ($[103] !== t32 || $[104] !== t33 || $[105] !== t34) {
    t35 = <>{t32}{t33}{t34}</>;
    $[103] = t32;
    $[104] = t33;
    $[105] = t34;
    $[106] = t35;
  } else {
    t35 = $[106];
  }
  let t36;
  if ($[107] !== controls || $[108] !== expanded || $[109] !== phase || $[110] !== t18 || $[111] !== t24 || $[112] !== t30 || $[113] !== t31 || $[114] !== t35) {
    t36 = <StageShell testId="call-stage" phase={phase} ariaLabel="Call" expanded={expanded} onExpandedChange={t18} expandTestId="call-expand" collapseTestId="call-collapse" expandedHeader={t24} panelHeader={t30} expandedBody={t31} panelBody={t35} controls={controls} />;
    $[107] = controls;
    $[108] = expanded;
    $[109] = phase;
    $[110] = t18;
    $[111] = t24;
    $[112] = t30;
    $[113] = t31;
    $[114] = t35;
    $[115] = t36;
  } else {
    t36 = $[115];
  }
  return t36;
}
function _temp22(f_2) {
  return <PanelFace key={f_2.key} face={f_2} />;
}
function _temp21(f_1) {
  return <StageTile key={f_1.key} face={f_1} />;
}
function _temp20(f_0) {
  return <PanelFace key={f_0.key} face={f_0} />;
}
function _temp19(f) {
  return <StageTile key={f.key} face={f} />;
}
function _temp18(v) {
  return !v;
}
function _temp17(u) {
  return u !== null;
}
function _temp16(p_1) {
  return p_1.userId;
}
function _temp15(el) {
  if (el) {
    callEngine.attachShareVideo(el);
  }
  return () => {
    if (el) {
      callEngine.detachShareVideo(el);
    }
  };
}
function _temp14(t) {
  return [t.userId, t.name];
}
function _temp13(s_11) {
  return s_11.patch;
}
function _temp12(s_10) {
  return s_10.errorMessage;
}
function _temp11(s_9) {
  return s_9.shareSurface;
}
function _temp10(s_8) {
  return s_8.shareLabel;
}
function _temp9(s_7) {
  return s_7.sharePresenterId;
}
function _temp8(s_6) {
  return s_6.shareTileId;
}
function _temp7(s_5) {
  return s_5.isSharing;
}
function _temp6(s_4) {
  return s_4.connectedAtMs;
}
function _temp5(s_3) {
  return s_3.isMuted;
}
function _temp4(s_2) {
  return s_2.peers;
}
function _temp3(s_1) {
  return s_1.conversationId;
}
function _temp2(s_0) {
  return s_0.expanded;
}
function _temp(s) {
  return s.phase;
}